import { Link } from "wouter";
import { ArrowRight, ChevronDown, CircleCheck, HeartHandshake, RefreshCw, ShieldCheck } from "lucide-react";
import heroFamily from "../../assets/landing/hero-family.webp";
import { cn } from "../../lib/cn";
import { FOCUS_RING } from "../../lib/focusRing";
import { CareCard } from "./CareCard";
import { Scene } from "./stage/Scene";
import { SplitWords } from "./stage/Split";
import * as fx from "./stage/fx";
import type { Timeline } from "./stage/choreography";

/*
 * Signature: masked word-rise headline + left-to-right ink wipes, then the
 * family opens through an iris. Copy first, picture second: everything moves
 * in reading order, one element at a time.
 */
const TIMELINE: Timeline = [
  fx.wipe("[data-fx=chip]", { duration: 0.45 }),
  fx.rise("[data-fx=h1a] [data-word]", { at: "-0.2" }),
  fx.wipe("[data-fx=h1b]", { duration: 0.65, at: "-0.25" }),
  fx.slide("[data-fx=sub]", { x: -28, blur: 6, at: "-0.3" }),
  fx.pop("[data-fx=cta]", { at: "-0.25" }),
  fx.wipe("[data-fx=trust]", { duration: 0.4, at: "-0.2" }),
  fx.pop("[data-fx=stage]", { from: 0.5, at: "-0.15" }),
  fx.iris("[data-fx=family]", { origin: "50% 92%", duration: 0.75, at: "-0.35" }),
  fx.fly("[data-fx=card]", { at: "-0.4" }),
  fx.pop("[data-fx=toast]", { stagger: 0.22, at: "-0.35" }),
  fx.fade("[data-fx=hint]", { at: "+0.1" }),
];

const ctaBase =
  "inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium shadow-soft transition-shadow sm:px-6 sm:py-3 sm:text-base";

export function Hero() {
  return (
    <Scene
      id="top"
      label="Welcome"
      timeline={TIMELINE}
      className="bg-gradient-to-b from-sky-50 via-white to-rose-50/60"
    >
      {/* Backdrop: soft colour fields and a whisper of grain. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -left-40 -top-40 h-[30rem] w-[30rem] rounded-full bg-sky-200/50 blur-3xl" />
        <div className="absolute -right-32 top-1/4 h-[28rem] w-[28rem] rounded-full bg-violet-200/40 blur-3xl" />
        <div className="absolute -bottom-24 left-1/4 h-80 w-80 rounded-full bg-rose-200/40 blur-3xl" />
        <div className="bg-grain absolute inset-0 opacity-[0.05] mix-blend-multiply" />
      </div>

      <div className="mx-auto grid w-full max-w-6xl flex-1 items-center gap-6 px-6 pb-10 pt-20 sm:gap-8 lg:grid-cols-[1.05fr_1fr] lg:gap-4 lg:pb-16">
        {/* Copy */}
        <div className="relative z-10">
          <span
            data-fx="chip"
            className="inline-flex items-center gap-2 rounded-full border border-rose-100 bg-white/85 px-3 py-1 text-xs font-medium text-neutral-600 shadow-soft backdrop-blur"
          >
            <HeartHandshake className="h-3.5 w-3.5 text-rose-500" aria-hidden="true" />
            For the whole family circle
          </span>

          <h1 className="mt-5 font-display text-[2.1rem] font-bold leading-[1.08] tracking-tight text-neutral-900 sm:text-5xl lg:text-[3.55rem]">
            <span data-fx="h1a" className="block">
              <SplitWords text="Everyone who cares for your family" mask />
            </span>
            <span
              data-fx="h1b"
              className="block bg-gradient-to-r from-sky-500 via-indigo-500 to-violet-500 bg-clip-text pb-1 text-transparent"
            >
              knows what you know.
            </span>
          </h1>

          <p data-fx="sub" className="mt-4 max-w-xl text-[15px] text-neutral-600 sm:mt-5 sm:text-lg">
            Keep your kids' allergies, medications, routines, and emergency contacts on one
            always-current list — then give grandparents, sitters, and teachers their own private
            link to just what they need.
          </p>

          <div className="mt-6 flex flex-wrap gap-3 sm:mt-7">
            <Link
              href="/register"
              data-fx="cta"
              className={cn(
                ctaBase,
                "bg-gradient-to-br from-sky-500 to-indigo-600 text-white hover:shadow-glow",
                FOCUS_RING,
              )}
            >
              Start your family's list <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
            <Link
              href="/login"
              data-fx="cta"
              className={cn(ctaBase, "border border-sky-200 bg-white text-sky-700 hover:shadow-glow", FOCUS_RING)}
            >
              Sign in
            </Link>
          </div>

          <p data-fx="trust" className="mt-4 flex items-center gap-2 text-sm text-neutral-500">
            <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-500" aria-hidden="true" />
            Free to start · No credit card · No app for caregivers
          </p>
        </div>

        {/* Picture: the family, the care sheet they share, and two live moments. */}
        <div className="relative mx-auto w-full max-w-[15rem] sm:max-w-[26rem] lg:max-w-[33rem]">
          <div
            data-fx="stage"
            aria-hidden="true"
            className="absolute inset-x-[4%] bottom-[2%] top-[14%] rounded-[45%] bg-gradient-to-br from-sky-200/80 via-violet-200/70 to-rose-200/80 blur-2xl"
          />
          <div data-fx="family" className="relative">
            <div className="animate-float-slow">
              <img
                src={heroFamily}
                alt="Illustration of a smiling family: grandparents, mom and dad, two children and their dog"
                width={1000}
                height={1000}
                className="h-auto w-full select-none"
                draggable={false}
              />
            </div>
          </div>

          <div data-fx="card" className="absolute -bottom-4 -right-8 w-28 sm:-bottom-6 sm:-right-2 sm:w-48 lg:-right-8 lg:w-56">
            <div className="rotate-3">
              <div className="rounded-[1.75rem] border border-white/70 bg-white/60 p-1.5 shadow-glow backdrop-blur-sm">
                <CareCard />
              </div>
            </div>
          </div>

          <div aria-hidden="true">
            <div data-fx="toast" className="absolute -left-6 top-[12%] hidden sm:block">
              <div className="flex animate-bob items-center gap-2.5 rounded-2xl border border-emerald-100 bg-white/90 px-3 py-2 shadow-soft backdrop-blur">
                <CircleCheck className="h-5 w-5 text-emerald-500" />
                <div className="text-left leading-tight">
                  <p className="text-xs font-semibold text-neutral-800">Grandma opened Ella's list</p>
                  <p className="text-[11px] text-neutral-500">just now</p>
                </div>
              </div>
            </div>
            <div data-fx="toast" className="absolute -right-4 top-[3%] hidden sm:block lg:-right-10">
              <div
                className="flex animate-bob items-center gap-2.5 rounded-2xl border border-sky-100 bg-white/90 px-3 py-2 shadow-soft backdrop-blur"
                style={{ animationDelay: "1.4s" }}
              >
                <RefreshCw className="h-[18px] w-[18px] text-sky-500" />
                <div className="text-left leading-tight">
                  <p className="text-xs font-semibold text-neutral-800">Bedtime routine updated</p>
                  <p className="text-[11px] text-neutral-500">Every link shows it instantly</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-5 left-1/2 hidden -translate-x-1/2 md:block">
        <a
          data-fx="hint"
          href="#circle"
          className={cn(
            "flex flex-col items-center gap-1 rounded-full px-3 py-1 text-xs font-medium text-neutral-500 hover:text-neutral-800",
            FOCUS_RING,
          )}
        >
          Scroll
          <ChevronDown className="h-4 w-4 animate-bounce" aria-hidden="true" />
        </a>
      </div>
    </Scene>
  );
}
